/**
 * Todas las llamadas al backend. Rutas relativas (`/api/...`): en desarrollo las
 * reenvia Vite, en produccion van al mismo dominio. La cookie de sesion viaja sola.
 */

import type {
  EstadoUsuario,
  Categoria,
  Usuario,
  ResultadoBusqueda,
  Cliente,
  Opciones,
  EntradaCotizacion,
  TipoCotizacion,
  OpcionesIndustrial,
  EntradaIndustrial,
  ResultadoCotizacion,
  ResumenCotizacion,
  CotizacionGuardada,
  EntradaOffgrid,
  OpcionesOffgrid,
  ResultadoOffgrid,
} from './tipos'
import type { ConsumoDelLead, Espera, FuenteConsumo, MesConsumo, ProyeccionCliente } from './tipos-prediccion'
import type { EstadoComercial, Periodo, Resumen, UtilidadResumen } from './tipos-dashboard'
import type { FilaCatalogo, ItemCatalogo, RespuestaCatalogo, TipoItem } from './tipos-catalogo'

export type * from './tipos-catalogo'
export type * from './tipos'
export type * from './tipos-prediccion'
export type * from './tipos-dashboard'

/** De donde sale el consumo y los supuestos del costo de esperar (rebanada 4). */
export interface ExtraCotizacion {
  clienteId?: string
  fuente?: FuenteConsumo
  espera?: Espera
}

const extra = (e: ExtraCotizacion = {}) => ({ cliente_id: e.clienteId, fuente_consumo: e.fuente ?? 'manual', espera: e.espera })

export class ErrorApi extends Error {
  constructor(
    public readonly estado: number,
    mensaje: string,
    public readonly datos: Record<string, unknown> = {},
  ) {
    super(mensaje)
  }
}

async function llamar<T>(metodo: string, ruta: string, cuerpo?: unknown): Promise<T> {
  let respuesta: Response
  try {
    respuesta = await fetch(ruta, {
      method: metodo,
      headers: cuerpo === undefined ? {} : { 'Content-Type': 'application/json' },
      body: cuerpo === undefined ? undefined : JSON.stringify(cuerpo),
      credentials: 'same-origin',
    })
  } catch {
    throw new ErrorApi(0, 'No hay conexion con el servidor.')
  }
  if (respuesta.status === 204) return undefined as T
  const datos = (await respuesta.json().catch(() => ({}))) as Record<string, unknown>
  if (!respuesta.ok) {
    throw new ErrorApi(respuesta.status, String(datos.error ?? 'Algo salio mal.'), datos)
  }
  return datos as T
}

export const api = {
  yo: () => llamar<{ usuario: Usuario }>('GET', '/api/auth/yo'),
  entrar: (correo: string, password: string) =>
    llamar<{ usuario: Usuario }>('POST', '/api/auth/login', { correo, password }),
  registrar: (nombre: string, correo: string, password: string) =>
    llamar<{ usuario: Usuario }>('POST', '/api/auth/registro', { nombre, correo, password }),
  salir: () => llamar<void>('POST', '/api/auth/logout'),

  usuarios: (estado?: EstadoUsuario) =>
    llamar<{ usuarios: Usuario[] }>('GET', `/api/usuarios${estado ? `?estado=${estado}` : ''}`),
  decidirUsuario: (id: string, accion: 'aprobar' | 'rechazar') =>
    llamar<{ usuario: Usuario }>('POST', `/api/usuarios/${id}/${accion}`),

  buscarClientes: (q: string) =>
    llamar<{ resultados: ResultadoBusqueda[] }>('GET', `/api/clientes?q=${encodeURIComponent(q)}`),
  crearCliente: (datos: {
    nombre: string
    identificacion: string
    telefono: string
    correo: string
    categoria: Categoria
    origen: 'panel' | 'web' | 'whatsapp'
    origen_id?: string
  }) => llamar<{ cliente: Cliente }>('POST', '/api/clientes', datos),
  cliente: (id: string) => llamar<{ cliente: Cliente }>('GET', `/api/clientes/${id}`),
  completarIdentificacion: (id: string, identificacion: string) =>
    llamar<{ cliente: Cliente }>('PATCH', `/api/clientes/${id}/identificacion`, { identificacion }),

  opciones: () => llamar<Opciones>('GET', '/api/cotizaciones/opciones'),
  opcionesIndustrial: () => llamar<OpcionesIndustrial>('GET', '/api/cotizaciones/opciones?tipo=industrial'),
  calcular: (entrada: EntradaCotizacion | EntradaIndustrial, tipo: TipoCotizacion = 'estandar', e?: ExtraCotizacion) =>
    llamar<{ resultado: ResultadoCotizacion }>('POST', '/api/cotizaciones/calcular', { tipo, entrada, ...extra(e) }),
  cotizaciones: (clienteId: string) =>
    llamar<{ cotizaciones: ResumenCotizacion[] }>('GET', `/api/clientes/${clienteId}/cotizaciones`),
  guardarCotizacion: (
    clienteId: string,
    entrada: EntradaCotizacion | EntradaIndustrial,
    nota: string,
    tipo: TipoCotizacion = 'estandar',
    e?: ExtraCotizacion,
  ) => llamar<{ cotizacion: CotizacionGuardada }>('POST', `/api/clientes/${clienteId}/cotizaciones`, { tipo, entrada, nota, ...extra(e) }),

  opcionesOffgrid: () => llamar<OpcionesOffgrid>('GET', '/api/cotizaciones/offgrid/opciones'),
  calcularOffgrid: (entrada: EntradaOffgrid) =>
    llamar<{ resultado: ResultadoOffgrid }>('POST', '/api/cotizaciones/offgrid/calcular', { entrada }),
  guardarOffgrid: (clienteId: string, entrada: EntradaOffgrid, nota: string) =>
    llamar<{ cotizacion: CotizacionGuardada }>('POST', `/api/clientes/${clienteId}/cotizaciones/offgrid`, { entrada, nota }),

  consumos: (clienteId: string) =>
    llamar<{ consumos: MesConsumo[]; lead: ConsumoDelLead | null }>('GET', `/api/clientes/${clienteId}/consumos`),
  guardarConsumos: (clienteId: string, consumos: Omit<MesConsumo, 'origen'>[] | MesConsumo[]) =>
    llamar<{ consumos: MesConsumo[] }>('PUT', `/api/clientes/${clienteId}/consumos`, { consumos }),
  proyectar: (clienteId: string) => llamar<ProyeccionCliente>('POST', `/api/clientes/${clienteId}/consumos/proyeccion`),

  catalogo: () => llamar<RespuestaCatalogo>('GET', '/api/catalogo'),
  crearItem: (item: { nombre: string; costo: number; grava_iva: boolean; tipo?: TipoItem; potencia_w?: number | null; capacidad_kwh?: number | null }) =>
    llamar<{ item: ItemCatalogo }>('POST', '/api/catalogo/items', item),
  // Nunca se borra: desactivar es `activo: false`.
  cambiarItem: (id: string, cambio: { nombre?: string; costo?: number; grava_iva?: boolean; activo?: boolean; tipo?: TipoItem; potencia_w?: number | null; capacidad_kwh?: number | null }) =>
    llamar<{ item: ItemCatalogo }>('PATCH', `/api/catalogo/items/${id}`, cambio),
  ajustarProducto: (codigo: string, ajuste: { costo: number | null; activo: boolean }) =>
    llamar<{ ajuste: Pick<FilaCatalogo, 'codigo' | 'activo'> & { costo: number | null } }>('PUT', `/api/catalogo/ajustes/${encodeURIComponent(codigo)}`, ajuste),

  resumen: (dias: Periodo) => llamar<Resumen>('GET', `/api/dashboard?dias=${dias}`),
  utilidad: (dias: Periodo) => llamar<{ utilidad: UtilidadResumen }>('GET', `/api/dashboard/utilidad?dias=${dias}`),
  marcarEstado: (cotizacionId: string, estado: EstadoComercial) =>
    llamar<{ cotizacion: CotizacionGuardada }>('PATCH', `/api/cotizaciones/${cotizacionId}/estado`, { estado }),
  cotizacion: (id: string) => llamar<{ cotizacion: CotizacionGuardada }>('GET', `/api/cotizaciones/${id}`),
}

/**
 * Descarga el documento. No es un <a href> a secas porque, si el servidor se niega
 * (409: el catalogo cambio), hay que mostrar el motivo en vez de bajar un JSON.
 */
export async function descargarDocumento(
  id: string,
  tipo: 'proforma' | 'informe',
  /** Por defecto el documento lleva todo; `false` quita esa parte (decision comercial por cliente). */
  incluir: { retorno?: boolean; espera?: boolean; detalle?: boolean } = {},
): Promise<void> {
  let respuesta: Response
  const quitar = [
    incluir.retorno === false && 'retorno=0',
    incluir.espera === false && 'espera=0',
    // Al reves que los otros dos: el detalle de items no sale salvo que se pida.
    incluir.detalle === true && 'detalle=1',
  ].filter(Boolean)
  try {
    respuesta = await fetch(`/api/cotizaciones/${id}/documento?${['tipo=' + tipo, ...quitar].join('&')}`, { credentials: 'same-origin' })
  } catch {
    throw new ErrorApi(0, 'No hay conexion con el servidor.')
  }
  if (!respuesta.ok) {
    const datos = (await respuesta.json().catch(() => ({}))) as Record<string, unknown>
    throw new ErrorApi(respuesta.status, String(datos.error ?? 'No se pudo generar el documento.'), datos)
  }
  const disposicion = respuesta.headers.get('content-disposition') ?? ''
  const nombre = /filename\*=UTF-8''([^;]+)/.exec(disposicion)?.[1]
  const url = URL.createObjectURL(await respuesta.blob())
  const enlace = document.createElement('a')
  enlace.href = url
  enlace.download = nombre ? decodeURIComponent(nombre) : `${tipo}.pdf`
  enlace.click()
  setTimeout(() => URL.revokeObjectURL(url), 10_000)
}
